import { tracking } from '@iwoca/frontend-tracking-library';
import {
  fetchGetProductOffers,
  fetchPostApplicationDecline,
  fetchPostFormalOffers,
  fetchPostIwocapayBuyerRefreshSpendingLimit,
} from '@iwoca/lapi-client/edge';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useStateKey } from '../../../hooks/useStateKey.hook';
import { getOutstandingApplicationErrorMessage } from '../../utils/outstandingApplicationError';
import {
  checkStatusIsDeclined,
  getApprovalRequirementErrors,
  getProductDeclineUid,
  getSuggestedProduct,
  selectApprovalRequirementErrorWithHighestPriority,
} from '../Dashboard.helpers';
import { ProductOffers, SuggestedProduct } from '../Dashboard.types';

export const GENERIC_REFRESH_ERROR_MESSAGE = 'Something went wrong';

const formaliseOffers = ({
  stateKey,
  offers,
}: {
  stateKey: string;
  offers: NonNullable<ProductOffers>['products'][number]['offers'];
}) => {
  return fetchPostFormalOffers({
    stateKey,
    body: { data: { offer_ids: offers.map((offer) => offer.id) } },
  });
};

const handleDecline = (
  stateKey: string,
  suggestedProduct: SuggestedProduct,
) => {
  const productDeclineUid = getProductDeclineUid(suggestedProduct);

  const declineStatus =
    suggestedProduct.status! === 'hard_declined'
      ? 'Hard Declined'
      : 'Soft Declined';

  return fetchPostApplicationDecline({
    stateKey: stateKey,
    body: {
      data: {
        status: declineStatus,
        product_type_decline_uid: productDeclineUid!,
      },
    },
  });
};

export const useRefreshSpendingLimits = (onRefreshComplete: () => void) => {
  const { stateKey } = useStateKey();
  const queryClient = useQueryClient();

  const {
    mutateAsync: refreshSpendingLimit,
    error,
    isPending,
  } = useMutation({
    mutationFn: async () => {
      if (!stateKey) {
        throw new Error('No stateKey was provided.');
      }

      // Fire tracking event
      tracking.ampli.refreshSpendingLimitButtonClicked();

      // Create application
      await fetchPostIwocapayBuyerRefreshSpendingLimit({ stateKey });

      // Run product offers
      const productOffers = await fetchGetProductOffers({ stateKey });
      const suggestedProduct = getSuggestedProduct(productOffers.data!);

      if (!suggestedProduct) {
        throw new Error('Missing suggested product');
      }

      // Handle approval requirements error
      const approvalRequirementErrors =
        getApprovalRequirementErrors(suggestedProduct);
      if (Array.from(approvalRequirementErrors.values()).length) {
        const highestPriorityError =
          selectApprovalRequirementErrorWithHighestPriority(
            approvalRequirementErrors,
          );
        throw new Error(highestPriorityError);
      }

      // Handle declines
      const isDeclined = checkStatusIsDeclined(suggestedProduct!);
      if (isDeclined) {
        await handleDecline(stateKey, suggestedProduct!);
      }

      // Formalise offers
      if (!isDeclined) {
        if (suggestedProduct.offers.length === 0) {
          throw new Error('No suggested product offers');
        }
        await formaliseOffers({
          stateKey,
          offers: suggestedProduct.offers,
        });
      }

      // Refresh data
      await queryClient.invalidateQueries({
        queryKey: ['fetchGetFormalOffers'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['fetchGetBuyerSpendingLimit'],
      });
      onRefreshComplete();
    },
  });

  const errorMessage = error
    ? (getOutstandingApplicationErrorMessage(error) ??
      GENERIC_REFRESH_ERROR_MESSAGE)
    : null;

  return {
    refreshSpendingLimit,
    isLoading: isPending,
    errorMessage,
  };
};
